import React, {useState, useEffect, useCallback} from "react";
import {
  Modal,
  Input,
  Form,
  Divider,
  Dimmer,
  Checkbox,
  Loader,
  Dropdown,
} from "semantic-ui-react";
import * as Yup from "yup";
import Asterisk from "../../components/common/Asterisk";
import { Formik, Form as FormikForm, FieldArray } from "formik";
import { useSelector } from "react-redux";
import { selectTranslations } from "../../config/i18n/slice";
import {
  CustomerPlusIcon,
  DeleteIcon,
  CloseIcon,
  SearchIcon,
  ArrowLeft,
  ArrowRight,
} from "../../../Assets/Icons/svg";
import useDisplayFormattedText from "../../../hooks/useDisplayFormattedText";
import _ from "lodash";
import DataTable from "react-data-table-component";
import Pagination from "../../components/common/Pagination";
import { toast } from "react-toastify";
import { scrollToError } from "../../../utilFunctions/scrollToError";
import { getAllParts } from "../../config/sharedApi/partApis";
import { currencyDecimalPlaces } from "../../config/common/commonVariables";
import { getAllActiveSets } from "../../config/api/setApis";
import { handleKeyDown } from "../../config/common/commonFunctions";

const positiveNumRegex = /^[1-9]\d*$/;
/*
 * The `SetForm` function in JavaScript defines a form component with various fields and functionalities for adding parts and sets, handling validations, and displaying modals for adding
 * parts and sets.
 */
const SetForm = ({
  initialFormValues,
  edit = false,
  cancelHandler = () => {},
  initPartsData,
  partsData = [],
  initSetsData,
  setsData = [],
  dropdownOptions,
  prevPartsData = [],
  prevSetsData = [],
  selectedTableSetData = [],
  selectedTablePartData = [],
  initChange,
  role,
  setNewCancelModal = () => {},
  setAllSetsData = () => {},
  setAllPartsData = () => {},
  handleAddSet = () => {},
  setInitChange = () => {},
  setSetsData = () => {},
  setInitSetsData = () => {},
  fetchLocations = () => {},
  setPrevPartsData = () => {},
  setPrevSetsData = () => {},
  setInitPartsData = () => {},
  setSelectedTableSetData = () => {},
  setSelectedTablePartData = () => {},
  setPartsData = () => {},
}) => {
  const t = useSelector(selectTranslations);
  const organisationCurrency = useSelector((state) => {
    if (state?.auth?.user?.organisation) {
      return state.auth.user.organisation.currency;
    } else {
      return "JOD";
    }
  });
  const organisationId = useSelector((state) => {
    if (state?.auth?.user?.organisation) {
      return state.auth.user.organisation.id;
    } else {
      return null;
    }
  });

  const handleDecimalPoints = (value) => {
    const decimalPlaces = currencyDecimalPlaces[organisationCurrency] ?? 2;
    const factor = Math.pow(10, decimalPlaces);
    const roundedValue = Math.round(value * factor) / factor;
    const formattedValue = roundedValue.toFixed(decimalPlaces);

    if (formattedValue.indexOf(".") !== -1) {
      return formattedValue.replace(/\.?0+$/, "");
    }

    return Number(formattedValue);
  };

  const selectedLanguage = useSelector((state) => {
    if (state?.i18n?.lang) {
      return state.i18n.lang;
    } else {
      return "en";
    }
  });

  const PersonValidationSchema = Yup.object().shape({
    name: Yup.string()
        .required(t.common.required_field)
        .min(3, t.common.minmax_chars?.replace("{min}", "3")?.replace("{max}", "150") || "Must be 3–150 characters")
        .max(150, t.common.minmax_chars?.replace("{min}", "3")?.replace("{max}", "150") || "Must be 3–150 characters"),

    locations: Yup.number()
        .typeError(t.common.required_field)
        .required(t.common.required_field),

    parts: Yup.array().of(
        Yup.object().shape({
          id: Yup.number().required(t.common.required_field),
          quantity: Yup.mixed()
              .required(t.common.required_field)
              .test(
                  "is-valid-quantity",
                  t.common.valid_digits,
                  (value) => {
                    if (value === undefined || value === null || value === "") return false;
                    const regex = /^\d{1,8}(\.\d{1,4})?$/;
                    return regex.test(value.toString());
                  }
              )
              .test(
                  "is-non-negative",
                  t.common.quantity_must_be_greater_than_zero,
                  (value) => {
                    if (value === undefined || value === null || value === "") return false;
                    const num = Number(value);
                    return num >= 0;
                  }
              ),
          totalCost: Yup.number()
              .nullable()
              .min(0, t.common.cost_must_be_greater_than_zero || "Total cost must be 0 or higher"),
        })
    ),

    sets: Yup.array().of(
        Yup.object().shape({
          id: Yup.number().required(t.common.required_field),
          quantity: Yup.mixed()
              .required(t.common.required_field)
              .test(
                  "is-valid-quantity",
                  t.common.valid_digits,
                  (value) => {
                    if (value === undefined || value === null || value === "") return false;
                    const regex = /^\d{1,8}(\.\d{1,4})?$/;
                    return regex.test(value.toString());
                  }
              )
              .test(
                  "is-positive",
                  t.common.quantity_must_be_greater_than_zero,
                  (value) => {
                    if (value === undefined || value === null || value === "") return false;
                    const num = Number(value);
                    return num > 0;
                  }
              ),
          totalCost: Yup.number()
              .nullable()
              .min(0, t.common.cost_must_be_greater_than_zero || "Total cost must be 0 or higher"),
        })
    ),
  });

  const getPaginatedData = (dataToPaginate) => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return dataToPaginate.slice(startIndex, endIndex);
  };
  const { DisplayFormattedText } = useDisplayFormattedText();

  const [addPartModal, setAddPartModal] = useState(false);
  const [noPartModal, setNoPartModal] = useState(false);
  const [searchTitle, setSearchTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [addSetModal, setAddSetModal] = useState(false);
  const [addSetModalTitle, setAddSetModalTitle] = useState(false);
  const [searchSetTitle, setSearchSetTitle] = useState("");

  let _newData = initPartsData;
  useEffect(() => {
    if (searchTitle.length > 0) {
      const re = new RegExp(_.escapeRegExp(searchTitle), "i");
      const isMatch = (result) => re.test(result?.name);
      _newData =
        searchTitle.length > 0 ? _.filter(_newData, isMatch) : _newData;
    }
    if (_newData?.length > 0) {
      setPartsData(_newData);
    } else {
      setPartsData([]);
    }
  }, [searchTitle]);

  let _newDataSets = initSetsData;
  useEffect(() => {
    const source = Array.isArray(initSetsData) ? initSetsData : [];
    const filtered = searchSetTitle
      ? source.filter((s) =>
            new RegExp(_.escapeRegExp(searchSetTitle), "i").test(s?.name)
        )
        : source;

    const selectedIds = new Set((selectedTableSetData || []).map((s) => s.id));
    const withSelection = filtered.map((s) => ({
          ...s,
          selectedSet: selectedIds.has(s.id),
    }));
    const sortBySelection = (arr) =>
        arr.sort((a, b) =>
            a.selectedSet === b.selectedSet ? 0 : a.selectedSet ? -1 : 1
        );
    setSetsData(withSelection.length ? sortBySelection(withSelection) : []);
    }, [searchSetTitle, initSetsData, selectedTableSetData]);

  /*
   * The `addParts` function in JavaScript fetches parts data, filters out selected parts, and updates the state accordingly and open add part modal.
   */
  const addParts = async (selectedPartsData = [], locations) => {
    setInitChange(true);

    setLoading(true);
    const fetchData = await getAllParts(
      organisationId,
      "",
      1,
      99999,
      [],
      [],
      [],
      [],
      [locations],
      [],
      [],
      [],
      "",
      [],
      role
    );

    if (fetchData?.response) {
      if (fetchData?.response?.length == 0) {
        setLoading(false);
        setNoPartModal(true);
      } else {
        const filteredParts = fetchData?.response?.flatMap((part) =>
          part?.attributes?.inventory?.length > 0
            ? part.attributes.inventory
                .filter((inv) => inv?.location?.data?.id == locations)
                .map((inv) => ({
                  id: part.id,
                  name: part.attributes.name,
                  totalCost:
                    inv?.unitCost !== null
                      ? handleDecimalPoints(inv?.unitCost)
                      : 0,
                  quantity: 1,
                  unitCost:
                    inv?.unitCost !== null
                      ? handleDecimalPoints(inv?.unitCost)
                      : 0,
                  unitType: part?.attributes?.measuringUnit,
                  selectedPart: false,
                }))
            : []
        );

        const filteredPartsData = filteredParts
          ?.map((part) => {
            let selectedPart = selectedPartsData.find(
              (el) => el?.id == part?.id
            );

            if (selectedPart) {
              return null;
            } else {
              return {
                selectedPart: false,
                id: part.id,
                name: part.name,
                totalCost: part?.totalCost,
                unitType: part?.unitType,
                quantity: 1,
                unitCost: part.unitCost,
              };
            }
          })
          .filter((part) => part !== null);

        setPartsData(filteredPartsData);
        setInitPartsData(filteredPartsData);
        setAllPartsData(filteredPartsData);
        setLoading(false);
        setAddPartModal(true);
      }
    } else {
      toast.error(t.common.something_wrong);
      setLoading(false);
      setAddPartModal(true);
    }
    setLoading(false);
  };
  /*
   * The `addParts` function in JavaScript fetches sets data, filters out selected sets, and updates the state accordingly and open add set modal.
   */
  const addSets = async (selectedSetsData = [], locations) => {
    setInitChange(true);

    setLoading(true);
    const fetchData = await getAllActiveSets(organisationId, locations);

    if (fetchData) {
      if (fetchData?.length == 0) {
        setLoading(false);
        setNoPartModal(true);
        setAddSetModalTitle(true);
      } else {
        const cleanSource = fetchData.map((set) => ({
          id: set.id,
          name: set.name,
          totalCost: handleDecimalPoints(set?.totalCost),
          quantity: 1,
          unitCost: handleDecimalPoints(set.totalCost),
          disable: set?.includeSet,
        }));
        setInitSetsData(cleanSource);
        const selectedIds = new Set((selectedSetsData || []).map((s) => s.id));
        const withSelection = cleanSource.map((s) => ({
          ...s,
          selectedSet: selectedIds.has(s.id),
        }));
        const sortBySelection = (arr) =>
            arr.sort((a, b) =>
                a.selectedSet === b.selectedSet ? 0 : a.selectedSet ? -1 : 1
            );
        setSetsData(sortBySelection(withSelection));
        setAllSetsData(cleanSource);
        setLoading(false);
        setAddSetModal(true);
        setAddSetModalTitle(true);
      }
    } else {
      toast.error(t.common.something_wrong);
      setLoading(false);
      setAddSetModal(true);
      setAddSetModalTitle(true);
    }
    setLoading(false);
  };

  const handleAddPartData = async (setFieldValue) => {
    setAddPartModal(false);
    setPrevPartsData([]);
    setSearchTitle("");
    setFieldValue("parts", selectedTablePartData);
  };

  const handleAddSetData = async (setFieldValue) => {
    setAddSetModal(false);
    setPrevSetsData([]);
    setSearchSetTitle("");
    setFieldValue("sets", selectedTableSetData);
    setAddSetModalTitle(false);
  };

  /*
   * The function `handleRowSelected` manages the selection of rows in a table, updating the data accordingly and moving selected rows to the top.
   */
  const handleRowSelected = (
    row,
    checked,
    setFieldValue,
    selectedPartsData = []
  ) => {
    let allSelectedRows = [...selectedPartsData];
    let selectedRows = [...prevPartsData];

    if (checked) {
      allSelectedRows.unshift({ ...row, selectedPart: true });
      selectedRows.unshift({ ...row, selectedPart: true });
    } else {
      allSelectedRows = allSelectedRows.filter((el) => el?.id !== row?.id);
      selectedRows = selectedRows.filter((el) => el?.id !== row?.id);
    }

    const updatedData = [...partsData];
    const updatedInitData = [...initPartsData];

    const selectedRowIds = allSelectedRows.map((row) => row.id);

    updatedData.forEach((item) => {
      if (selectedRowIds?.length === 0) {
        item.selectedPart = false;
      } else {
        item.selectedPart = selectedRowIds.includes(item.id);
      }
    });

    updatedInitData.forEach((item) => {
      if (selectedRowIds?.length === 0) {
        item.selectedPart = false;
      } else {
        item.selectedPart = selectedRowIds.includes(item.id);
      }
    });

    selectedRowIds.forEach((selectedRowId, index) => {
      const selectedIndex = updatedData.findIndex(
        (item) => item.id === selectedRowId
      );
      if (selectedIndex !== -1 && selectedIndex !== index) {
        const [selectedRow] = updatedData.splice(selectedIndex, 1);
        updatedData.splice(index, 0, selectedRow);
      }

      const selectedPartIndex = updatedData.findIndex(
        (item) => item.id === selectedRowId
      );
      if (selectedPartIndex !== -1) {
        updatedData[selectedPartIndex].selectedPart = true;
      }

      const selectedInitIndex = updatedInitData.findIndex(
        (item) => item.id === selectedRowId
      );
      if (selectedInitIndex !== -1 && selectedInitIndex !== index) {
        const [selectedRow] = updatedInitData.splice(selectedInitIndex, 1);
        updatedInitData.splice(index, 0, selectedRow);
      }
      const selectedInitPartIndex = updatedInitData.findIndex(
        (item) => item.id === selectedRowId
      );
      if (selectedInitPartIndex !== -1) {
        updatedInitData[selectedInitPartIndex].selectedPart = true;
      }
    });

    setPartsData(updatedData);
    setInitPartsData(updatedInitData);
    setSelectedTablePartData(allSelectedRows);
    setPrevPartsData(selectedRows);
  };

  /*
   * The function `handleClosePartModal` filters out parts that are not present in `newAddedData` and
   * updates the state variables accordingly.
   */
  const handleClosePartModal = () => {
    let newAddedData = [...prevPartsData];
    let parts = [...selectedTablePartData];

    parts = parts?.filter(
      (part) => !newAddedData.some((addedPart) => addedPart.id == part.id)
    );

    setAddPartModal(false);
    setSearchTitle("");

    setSelectedTablePartData(parts);
  };
  /*
   * The function `handleSetRowSelected` manages the selection of rows in a table by updating the data
   * and moving selected rows to the top.
   */

  const handleSetRowSelected = useCallback(
      (row, checked, _ignoredSetFieldValue, selectedSetsData = []) => {
        if (!row || !row.id) return;

        const incoming = Array.isArray(selectedSetsData) ? selectedSetsData : [];
        let allSelectedRows = [...incoming];
        let selectedRows = Array.isArray(prevSetsData) ? [...prevSetsData] : [];

        if (checked) {
          if (!allSelectedRows.some((item) => item.id === row.id)) {
            allSelectedRows = [{ ...row, selectedSet: true }, ...allSelectedRows];
            selectedRows = [{ ...row, selectedSet: true }, ...selectedRows];
          }
        } else {
          allSelectedRows = allSelectedRows.filter((el) => el.id !== row.id);
          selectedRows = selectedRows.filter((el) => el.id !== row.id);
        }

        const selectedRowIds = new Set(allSelectedRows.map((r) => r.id));
        const updateSelectionState = (arr) =>
            arr.map((item) => ({ ...item, selectedSet: selectedRowIds.has(item.id) }));

        const updatedData = updateSelectionState([...setsData]);

        const sortBySelection = (arr) =>
            arr.sort((a, b) => (a.selectedSet === b.selectedSet ? 0 : a.selectedSet ? -1 : 1));

        setSetsData(sortBySelection(updatedData));
        setSelectedTableSetData(allSelectedRows);
        setPrevSetsData(selectedRows);
      },
      [setsData, prevSetsData, setSetsData, setSelectedTableSetData, setPrevSetsData]
  );

  /*
   * The function `handleCloseSetModal` removes selected parts from a list and closes a modal.
   */
  const handleCloseSetModal = () => {
    let newAddedData = [...prevSetsData];
    let parts = [...selectedTableSetData];

    parts = parts?.filter(
      (part) => !newAddedData.some((addedPart) => addedPart.id == part.id)
    );

    setAddSetModal(false);
    setSearchSetTitle("");

    setSelectedTableSetData(parts);
  };
  /*
   * The function `removeFromSelectedParts` removes an item from an array of selected parts data at a
   * specified index and updates the state accordingly.
   */

  const removeFromSelectedParts = (
    index,
    selectedPartsData = [],
    setFieldValue = () => {}
  ) => {
    const updatedSelectedPartsData = [...selectedPartsData];
    updatedSelectedPartsData[index].selectedPart = false;
    updatedSelectedPartsData.splice(index, 1);
    setFieldValue("parts", updatedSelectedPartsData);
    handleRowSelected(null, false, setFieldValue, updatedSelectedPartsData);
  };

  /*
   * The function `removeFromSelectedSets` removes an item from an array of selected sets data and
   * updates the state accordingly.
   */
  const removeFromSelectedSets = (
    index,
    selectedSetsData = [],
    setFieldValue = () => {}
  ) => {
    const updatedSelectedSetsData = [...selectedSetsData];
    updatedSelectedSetsData[index].selectedSet = false;
    updatedSelectedSetsData.splice(index, 1);
    setFieldValue("sets", updatedSelectedSetsData);
    handleSetRowSelected(null, false, setFieldValue, updatedSelectedSetsData);
  };

  let allQtyOptions = [
    {
      key: 0,
      value: "pieces",
      text: t.parts.qty_unit.piece,
    },
    {
      key: 1,
      value: "items",
      text: t.parts.qty_unit.items,
    },
    {
      key: 2,
      value: "units",
      text: t.parts.qty_unit.unit,
    },
    {
      key: 3,
      value: "liters (L)",
      text: selectedLanguage == "en" ? "l" : "لتر",
    },
    {
      key: 4,
      value: "gallons (gal)",
      text: selectedLanguage == "en" ? "gal" : "جالون",
    },
    {
      key: 5,
      value: "cubic meters (m³)",
      text: selectedLanguage == "en" ? "m³" : "م³",
    },
    {
      key: 6,
      value: "cubic feet (ft³)",
      text: selectedLanguage == "en" ? "ft³" : "ق³",
    },
    {
      key: 7,
      value: "barrels",
      text: t.parts.vol_unit.barrels,
    },
    {
      key: 8,
      value: "kilograms (kg)",
      text: selectedLanguage == "en" ? "kg" : "كجم",
    },
    {
      key: 9,
      value: "pounds (lb)",
      text: selectedLanguage == "en" ? "lb" : "رطل",
    },

    {
      key: 10,
      value: "tons",
      text: t.parts.mass_unit.tons,
    },
    {
      key: 11,
      value: "metric tonnes",
      text: t.parts.mass_unit.m_tons,
    },
  ];

  const HandleUnitType = (data) => {
    let unitType;
    if (data) {
      unitType = allQtyOptions?.find((el) => el.value == data);
    }
    return data ? unitType?.text : null;
  };

  return (
    <>
      <Formik
        initialValues={initialFormValues}
        enableReinitialize={true}
        validationSchema={PersonValidationSchema}
        onSubmit={(values, { resetForm }) => {
          let data = handleAddSet(values, resetForm);
        }}
        validate={(values) => {
          const validationErrors = {};
          try {
            PersonValidationSchema.validateSync(values, { abortEarly: false });
          } catch (err) {
            scrollToError();
          }
          return validationErrors;
        }}
      >
        {({ errors, touched, setFieldValue, values, setFieldTouched }) => (
          <FormikForm>
            <div className="body p-6">
              <Form.Field>
                <label className="label-item">
                  {t.categories.table.name}

                  <Asterisk color="#172B4D" />
                </label>
                <Input
                  autoComplete="new-password"
                  fluid
                  placeholder={t.sets.form.add_name}
                  value={values?.name}
                  onChange={(e, value) => setFieldValue("name", e.target.value)}
                  error={errors.name && touched.name}
                  onFocus={(e) => {
                    setInitChange(true);
                    setFieldTouched("name", true);
                  }}
                />
                {errors.name && touched.name ? (
                  <div className="label-error">{errors.name}</div>
                ) : (
                  ""
                )}
              </Form.Field>

              <Form.Field>
                <label className="label-item">
                  {t.parts.form.inv_loc}
                  <Asterisk color="#172B4D" />
                </label>
                <Dropdown
                  placeholder={t.parts.form.add_inv_loc}
                  noResultsMessage={t.common.no_results_found}
                  fluid
                  search
                  clearable
                  selection
                  className="loc-dropdown"
                  options={dropdownOptions?.locations}
                  value={values?.locations}
                  onChange={(e, value) => {
                    setFieldValue("locations", value.value);
                    setFieldValue(`parts`, [], false);
                    setFieldValue(`sets`, [], false);
                    setSelectedTablePartData([]);
                    setSelectedTableSetData([]);
                  }}
                  onOpen={() => fetchLocations()}
                  error={errors.locations && touched.locations}
                  onBlur={(e) => {
                    setFieldTouched("locations", true);
                  }}
                />
                {errors.locations && touched.locations ? (
                  <div className="label-error">{errors.locations}</div>
                ) : (
                  ""
                )}
              </Form.Field>
              <label className="font-weight-bold label-item">
                {t.parts.tab.first}
              </label>

              {values?.parts?.length > 0 ? (
                <>
                  <FieldArray
                    name="parts"
                    render={(arrayHelpers) => (
                      <div>
                        <div className={"mt-6"}>
                          <div className={`kanbar-header`}>
                            <div class="grid-container-element set">
                              <div class="grid-child-element set">
                                {t.parts.table.name}
                              </div>
                              <div class="grid-child-element set">
                                {t.sets.form.qty}
                              </div>
                              <div class="grid-child-element set">
                                {t.parts.form.total_cost}
                              </div>
                              <div class="grid-child-element set"></div>
                            </div>
                          </div>
                        </div>
                        {values.parts.map((part, index) => (
                          <div
                            className="mt-1 grid-container-element set form"
                            key={index}
                          >
                            <div
                              className="grid-child-element set form "
                           
                            >
                              <span className="text-center">
                                {DisplayFormattedText(part?.name, 18)}
                              </span>
                            </div>
                            <div className="grid-child-element set form input-data">
                              <Input
                                autoComplete="new-password"
                               
                                className="d-flex qty-input"
                                type="number"
                                onKeyDown={(evt) => handleKeyDown(evt)}
                                value={part?.quantity}
                                onChange={(e) => {
                                  if (Number(e.target.value)) {
                                    let value = parseFloat(
                                      e.target.value
                                    ).toFixed(4);
                                    let total_cost = handleDecimalPoints(
                                      e.target.value * part.unitCost
                                    );
                                    setFieldValue(
                                      `parts[${index}].quantity`,
                                      Number(value)
                                    );
                                    setFieldValue(
                                      `parts[${index}].totalCost`,
                                      Number(total_cost),
                                      false
                                    );
                                  } else {
                                    setFieldValue(
                                      `parts[${index}].quantity`,
                                      ""
                                    );
                                    setFieldValue(
                                      `parts[${index}].totalCost`,
                                      part.unitCost
                                    );
                                  }
                                }}
                                error={
                                  errors &&
                                  errors?.parts &&
                                  errors?.parts?.length > 0 &&
                                  errors?.parts[index] &&
                                  errors?.parts[index]?.quantity
                                }
                                onFocus={(e) => {
                                  setFieldTouched(
                                    `parts[${index}].quantity`,
                                    true
                                  );
                                }}
                              >
                                <input />

                                <span
                                  className={`unitType align-items-center justify-content-center ${
                                    selectedLanguage == "en" ? "ml-2" : "mr-2"
                                  }`}
                                 
                                >
                                  {HandleUnitType(part?.unitType)}
                                </span>
                              </Input>
                              {errors &&
                              errors?.parts &&
                              errors?.parts?.length > 0 &&
                              errors?.parts[index]?.quantity ? (
                                <span className="label-error er-2">
                                  {errors?.parts[index]?.quantity}
                                </span>
                              ) : null}
                            </div>

                            <div
                              className={
                                selectedLanguage == "en"
                                  ? "grid-child-element  set  form input-data mt-2 ml-4"
                                  : "grid-child-element set form  input-data  mt-2 mr-4"
                              }
                            >
                              <span className="number-cost-set mt-2">
                                {DisplayFormattedText(
                                  String(part?.totalCost),
                                  7
                                )}
                              </span>
                              <span
                                className={`unitType align-items-center justify-content-center ${
                                  selectedLanguage == "en" ? "ml-2" : "mr-2"
                                }`}
                              
                              >
                                {organisationCurrency}
                              </span>
                            </div>

                            <div
                              className="grid-child-element set form input-data mt-2"
                              onClick={() => {
                                arrayHelpers.remove(index);
                                removeFromSelectedParts(
                                  index,
                                  values.parts,
                                  setFieldValue
                                );
                              }}
                            >
                              <DeleteIcon />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </>
              ) : null}

              <button
                className={`buttun-type-link bg-transparent mt-3`}
                basic
                type="button"
                disabled={!values?.locations}
                onClick={() =>
                  addParts(selectedTablePartData, values?.locations)
                }
              >
                <span className="customer-add-btn">
                  {" "}
                  <CustomerPlusIcon />{" "}
                </span>

                <p className={selectedLanguage == "en" ? "ml-2" : "mr-2"}>
                  {t.sets.form.add_parts}
                </p>
              </button>

              <Divider />
              <label  className="font-weight-bold label-item">
                {t.parts.tab.second}
              </label>

              {values?.sets?.length > 0 ? (
                <>
                  <FieldArray
                    name="sets"
                    render={(arrayHelpers) => (
                      <div>
                        <div className={"mt-6"}>
                          <div className={`kanbar-header`}>
                            <div class="grid-container-element set">
                              <div class="grid-child-element set">
                                {t.parts.table.name}
                              </div>
                              <div class="grid-child-element set">
                                {t.sets.form.qty}
                              </div>
                              <div class="grid-child-element set">
                                {t.parts.form.total_cost}
                              </div>
                              <div class="grid-child-element set"></div>
                            </div>
                          </div>
                        </div>
                        {values.sets.map((part, index) => (
                          <div
                            className="mt-1 grid-container-element set form"
                            key={index}
                          >
                            <div
                              className="grid-child-element set form "
                           
                            >
                              <span className="text-center">
                                {DisplayFormattedText(part?.name, 18)}
                              </span>
                            </div>
                            <div className="grid-child-element set form input-data">
                              <Input
                                autoComplete="new-password"
                               
                                className="d-flex"
                                type="number part-qty"
                                onKeyDown={(evt) => handleKeyDown(evt)}
                                value={part?.quantity}
                                onChange={(e) => {
                                  if (Number(e.target.value)) {
                                    let value = parseFloat(
                                      e.target.value
                                    ).toFixed(4);
                                    let total_cost = handleDecimalPoints(
                                      e.target.value * part.unitCost
                                    );
                                    setFieldValue(
                                      `sets[${index}].quantity`,
                                      Number(value)
                                    );
                                    setFieldValue(
                                      `sets[${index}].totalCost`,
                                      Number(total_cost),
                                      false
                                    );
                                  } else {
                                    setFieldValue(
                                      `sets[${index}].quantity`,
                                      ""
                                    );
                                    setFieldValue(
                                      `sets[${index}].totalCost`,
                                      part.unitCost
                                    );
                                  }
                                }}
                                error={
                                  errors &&
                                  errors?.sets &&
                                  errors?.sets?.length > 0 &&
                                  errors?.sets[index]?.quantity
                                }
                                onFocus={(e) => {
                                  setFieldTouched(
                                    `sets[${index}].quantity`,
                                    true
                                  );
                                }}
                              >
                                <input />
                              </Input>
                              {errors &&
                              errors?.sets &&
                              errors?.sets?.length > 0 &&
                              errors?.sets[index]?.quantity ? (
                                <span className="label-error">
                                  {errors?.sets[index]?.quantity}
                                </span>
                              ) : null}
                            </div>
                            <div
                              className={
                                selectedLanguage == "en"
                                  ? "grid-child-element   p-0 set form input-data mt-3 ml-4"
                                  : "grid-child-element  p-0 set  form input-data mt-3"
                              }
                             
                            >
                              <span className="number-cost-set mt-2">
                                {DisplayFormattedText(
                                  String(part?.totalCost),
                                  6
                                )}
                              </span>
                              <span
                                className={`unitType align-items-center justify-content-center ${
                                  selectedLanguage == "en" ? "ml-2" : "mr-2"
                                }`}
                              
                              >
                                {organisationCurrency}
                              </span>
                            </div>

                            <div
                              onClick={() => {
                                arrayHelpers.remove(index);
                                removeFromSelectedSets(
                                  index,
                                  values.sets,
                                  setFieldValue
                                );
                              }}
                              className="grid-child-element set form input-data mt-2"
                            >
                              <DeleteIcon />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </>
              ) : null}
              <button
                className={`buttun-type-link bg-transparent mt-3`}
                basic
                type="button"
                disabled={!values?.locations}
                onClick={() => addSets(selectedTableSetData, values?.locations)}
              >
                <span className="customer-add-btn">
                  {" "}
                  <CustomerPlusIcon />{" "}
                </span>

                <p className={selectedLanguage == "en" ? "ml-2" : "mr-2"}>
                  {t.sets.form.add_sets}
                </p>
              </button>

              <div className="form-button-container mt-10">
                <button
                  type="button"
                  onClick={() =>
                    initChange ? setNewCancelModal(true) : cancelHandler()
                  }
                  className="form-action-button"
                >
                  {t.assets.form.cancel}
                </button>

                <button type="submit" className="form-action-button blue">
                  {edit ? t.common.edit : t.common.add}
                </button>
              </div>

              {/* Add parts and add sets modal */}

              <Modal
                size="tiny"
                open={addPartModal}
                className="assets-modal part-list-modal set"
                onClose={() => {
                  handleClosePartModal();
                }}
              >
                <div className="header-container">
                  <span className="assets-header">{t.sets.form.add_parts}</span>
                  <div>
                    <span
                      className="c-pointer text-right"
                      onClick={() => {
                        handleClosePartModal();
                      }}
                    >
                      <CloseIcon />
                    </span>
                  </div>
                </div>

                <Modal.Content className="mt-2">
                  <div className="set-input">
                    <span className="search-svg-set">
                      <SearchIcon />
                    </span>
                    <Input
                      placeholder={t.sets.form.search_part}
                      value={searchTitle}
                      onChange={(e) => setSearchTitle(e.target.value)}
                    />
                  </div>
                  {loading ? (
                    <Dimmer active inverted>
                      <Loader inverted content={t.common.loading} />
                    </Dimmer>
                  ) : (
                    <>
                      <div className="parts-listing-container mt-5  bulk-upload-table">
                        <DataTable
                          columns={[
                            {
                              width: "20px",
                              cell: (row) => (
                                <div className="d-flex align-items-center">
                                  <div>
                                    <Checkbox
                                      checked={row?.selectedPart}
                                      onChange={(e, value) => {
                                        handleRowSelected(
                                          row,
                                          value.checked,
                                          setFieldValue,
                                          selectedTablePartData
                                        );
                                      }}
                                    />
                                  </div>
                                </div>
                              ),
                            },
                            {
                              minWidth: "350px",

                              sortField: "name",
                              sortable: true,
                              cell: (row) => (
                                <div className="d-flex align-items-center">
                                  <div>
                                    <span className="id-text-style">
                                      {row?.name ? row?.name : ""}
                                    </span>
                                  </div>
                                </div>
                              ),
                            },
                          ]}
                          data={getPaginatedData(partsData)}
                          noHeader={true}
                          noTableHead={true}
                          onRowClicked={(row) =>
                            handleRowSelected(
                              row,
                              row?.selectedPart ? false : true,
                              setFieldValue,
                              selectedTablePartData
                            )
                          }
                          noDataComponent={<span>{t.sets.no_data}</span>}
                        />
                      </div>

                      <div className="d-flex justify-content-end">
                        <Pagination
                          ofString={t.common.of}
                          total={partsData.length}
                          prevItem={
                            selectedLanguage === "en" ? (
                              <ArrowLeft />
                            ) : (
                              <ArrowRight />
                            )
                          }
                          nextItem={
                            selectedLanguage === "en" ? (
                              <ArrowRight />
                            ) : (
                              <ArrowLeft />
                            )
                          }
                          page={currentPage}
                          limit={pageSize}
                          handleNext={() => {
                            setCurrentPage(currentPage + 1);
                          }}
                          handlePrevious={() => {
                            setCurrentPage(currentPage - 1);
                          }}
                          parts={true}
                        />
                      </div>
                    </>
                  )}
                </Modal.Content>
                {!loading && (
                  <div
                    className={`modal-button-container ${
                      selectedLanguage == "en" ? "mr-8" : "ml-10"
                    }`}
                  >
                    <button
                      className="modal-form-action-button black"
                      onClick={() => {
                        handleClosePartModal();
                      }}
                    >
                      {t.assets.form.cancel}
                    </button>

                    <button
                      onClick={() => handleAddPartData(setFieldValue)}
                      className="modal-form-action-button light-blue"
                    >
                      {t.common.add}
                    </button>
                  </div>
                )}
              </Modal>

              <Modal
                size="tiny"
                open={addSetModal}
                className="assets-modal part-list-modal set"
                onClose={() => {
                  handleCloseSetModal();
                }}
              >
                <div className="header-container">
                  <span className="assets-header">{t.sets.form.add_sets}</span>
                  <div>
                    <span
                      className="c-pointer text-right"
                      onClick={() => {
                        handleCloseSetModal();
                      }}
                    >
                      <CloseIcon />
                    </span>
                  </div>
                </div>

                <Modal.Content className="mt-2">
                  <div className="set-input">
                    <span className="search-svg-set">
                      <SearchIcon />
                    </span>
                    <Input
                      placeholder={t.sets.search_sets}
                      value={searchSetTitle}
                      onChange={(e) => setSearchSetTitle(e.target.value)}
                    />
                  </div>
                  {loading ? (
                    <Dimmer active inverted>
                      <Loader inverted content={t.common.loading} />
                    </Dimmer>
                  ) : (
                    <>
                      <div className="parts-listing-container mt-5  bulk-upload-table">
                        <DataTable
                          className="wo-table-div"
                          columns={[
                            {
                              width: "20px",
                              sortField: "name",
                              cell: (row) => (
                                <div className="d-flex align-items-center">
                                  <div>
                                    <Checkbox
                                      checked={row?.selectedSet}
                                      disabled={row?.disable}
                                      className="set-checkbox-gery"
                                      onChange={(e, value) => {
                                        if (!row?.disable) {
                                          handleSetRowSelected(
                                            row,
                                            value.checked,
                                            setFieldValue,
                                            selectedTableSetData
                                          );
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              ),
                            },
                            {
                              minWidth: "350px",
                              sortField: "name",
                              sortable: true,
                              cell: (row) => (
                                <div
                                  onClick={() => {
                                    if (!row?.disable) {
                                      handleSetRowSelected(
                                        row,
                                        row?.selectedSet ? false : true,
                                        setFieldValue,
                                        selectedTableSetData
                                      );
                                    }
                                  }}
                                  className={`d-flex align-items-center justify-content-between widt-avai-part ${
                                    row?.disable ? "" : "margin-top-negative"
                                  }`}
                                 
                                >
                                  <h3
                                  
                                    className={`name-part ${
                                      row?.disable ? "margin" : ""
                                    }`}
                                   
                                  >
                                    {row?.name ? row?.name : ""}
                                  </h3>
                                  {row?.disable ? (
                                    <div
                                    
                                      className={"disable-txt-part"}
                                    >
                                      {t.sets.form.already}
                                    </div>
                                  ) : null}
                                </div>
                              ),
                            },
                          ]}
                          data={getPaginatedData(setsData)}
                          noHeader={true}
                          noTableHead={true}
                          onRowClicked={(row) => {
                            if (!row?.disable) {
                              handleSetRowSelected(
                                row,
                                row?.selectedSet ? false : true,
                                setFieldValue,
                                selectedTableSetData
                              );
                            }
                          }}
                          noDataComponent={<span>{t.sets.no_data}</span>}
                        />
                      </div>

                      <div className="d-flex justify-content-end">
                        <Pagination
                          ofString={t.common.of}
                          total={setsData.length}
                          prevItem={
                            selectedLanguage === "en" ? (
                              <ArrowLeft />
                            ) : (
                              <ArrowRight />
                            )
                          }
                          nextItem={
                            selectedLanguage === "en" ? (
                              <ArrowRight />
                            ) : (
                              <ArrowLeft />
                            )
                          }
                          page={currentPage}
                          limit={pageSize}
                          handleNext={() => {
                            setCurrentPage(currentPage + 1);
                          }}
                          handlePrevious={() => {
                            setCurrentPage(currentPage - 1);
                          }}
                          parts={true}
                        />
                      </div>
                    </>
                  )}
                </Modal.Content>
                {!loading && (
                  <div
                    className={`modal-button-container ${
                      selectedLanguage == "en" ? "mr-8" : "ml-10"
                    }`}
                  >
                    <button
                      className="modal-form-action-button black"
                      onClick={() => {
                        handleCloseSetModal();
                      }}
                    >
                      {t.assets.form.cancel}
                    </button>

                    <button
                      onClick={() => handleAddSetData(setFieldValue)}
                      className="modal-form-action-button light-blue"
                    >
                      {t.common.add}
                    </button>
                  </div>
                )}
              </Modal>
            </div>{" "}
          </FormikForm>
        )}
      </Formik>
      {/* open modal if no part or set added*/}
      <Modal
        size="tiny"
        className="assets-modal"
        open={noPartModal}
        onClose={() => {
          setNoPartModal(false);
          setAddSetModalTitle(false);
        }}
      >
        <div className="header-container">
          <span className="assets-header">
            {addSetModalTitle ? t.sets.add_set : t.parts.create_part}
          </span>

          <div>
            <span
              className="c-pointer text-right"
              onClick={() => {
                setNoPartModal(false);
                setAddSetModalTitle(false);
              }}
            >
              <CloseIcon />
            </span>
          </div>
        </div>
        <Modal.Content>
          <span className="assets-label ml-1">
            {addSetModalTitle ? t.sets.no_set : t.sets.no_part}
          </span>
        </Modal.Content>

        <div
          className={`modal-button-container mt-3 ${
            selectedLanguage == "en" ? "mr-6" : "ml-10"
          }`}
        >
          <button
            onClick={() => {
              setNoPartModal(false);
              setAddSetModalTitle(false);
            }}
            className="modal-form-action-button light-blue"
          >
            {t.assets.form.cancel}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default SetForm;
